import {InputFileAcceptMimes, InputFileAcceptTypes, UploadStates} from "../../../../../core/constants/enums";
import React, {useState} from "react";
import {Col, Row} from "reactstrap";
import Removable from "../../removable";
import {ReactComponent as ImagePlaceholder} from "../../../../../assets/images/dropzone/upload-placeholder.svg";
import Button from "../../../base/button/button";
import DropZone from "../../../base/dropzone";
import {openLinkInNewTab} from "../../../../../core/services/utils/utils";
import Utils from "../../../../../core/services/utils/utils";
import {ReactComponent as Pdf} from "../../../../../assets/images/warranty-files/pdf.svg";
import {ReactComponent as Video} from "../../../../../assets/images/warranty-files/video.svg";
import {ReactComponent as Unknown} from "../../../../../assets/images/warranty-files/php.svg";
import {ReactComponent as Zip} from "../../../../../assets/images/warranty-files/zip.svg";
import {UpsertPartSupportRequestDocumentFileTypes} from "../../part-support/part-support-request-doc";

const AddWarrantyAndReturnDocumentationDropZone = ({
                                                       disabled,
                                                       accept = [
                                                           InputFileAcceptMimes.images,
                                                           InputFileAcceptTypes.heic,
                                                           InputFileAcceptTypes.heif
                                                       ],
                                                       onSuccess,
                                                       multiple = false,
                                                       onRemove,
                                                       uploading,
                                                       uploadState,
                                                       tempUrl,
                                                       setTempUrl,
                                                       onEdit,
                                                       doc,
                                                   }) => {
    const [loading, setLoading] = useState(false);
    const {id, url, fileName} = (doc ?? {});
    const typeId = doc.selectedTypeId ?? doc?.validFileType?.id
    const hasError = uploadState?.state === UploadStates.error;


    /**
     * Removes the image and also stops the event from emitting.
     * @param e {MouseEvent}
     */
    const remove = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (onRemove) onRemove();
    }

    /**
     * Previews the selected file.
     *
     * opens the file in a new tab of the browser
     */
    const previewFile = () => {
        openLinkInNewTab(url)
    }

    /**
     * Renders the file icon based on the provided type.
     * @return {JSX.Element}
     */
    const renderFileIcon = () => {
        switch (typeId) {
            case UpsertPartSupportRequestDocumentFileTypes.pdf:
                return <Pdf className={'file-icon'}/>;
            case UpsertPartSupportRequestDocumentFileTypes.video:
                return <Video className={'file-icon'}/>;
            case UpsertPartSupportRequestDocumentFileTypes.compressed:
                return <Zip className={'file-icon'}/>;
            default:
                return <Unknown className={'file-icon'}/>;
        }
    }

    /**
     * Renders the inner file of the dropzone if the url is available.
     * @return {JSX.Element}
     */
    const renderFile = () => {
        switch (typeId) {
            case UpsertPartSupportRequestDocumentFileTypes.pdf:
            case UpsertPartSupportRequestDocumentFileTypes.video:
            case UpsertPartSupportRequestDocumentFileTypes.compressed:
            default: // UpsertPartSupportRequestDocumentFileTypes.everyFormat
                return <div
                    onClick={previewFile}
                    className={'file'}>
                    {renderFileIcon()}
                    <p>{fileName}</p>
                </div>
            case UpsertPartSupportRequestDocumentFileTypes.image:
                return <img
                    onClick={onEdit}
                    className='cursor-pointer-hover'
                    src={Utils.encodeFileUrl(url)}
                    alt={`dropzone-${id}`}
                />
        }
    }

    /**
     * Renders the upload progress ui when an image is being uploaded.
     * @return {JSX.Element}
     */
    const renderUploadProgress = () => {
        return (
            <div className={'d-flex flex-column align-items-center justify-content-center cursor-progress-hover'}>
                <span
                    className={`text-md font-weight-bold position-absolute z-index-1 ${uploadState.state === UploadStates.error ? "red" : ''}`}
                >
                    {uploadState.state === UploadStates.pending ? ((uploadState.percentage ?? 0) + '%') : uploadState.state}
                </span>
                {
                    tempUrl?.length > 0 && [UpsertPartSupportRequestDocumentFileTypes.image, UpsertPartSupportRequestDocumentFileTypes.everyFormat].includes(typeId)
                        ? <img
                            id={id}
                            className='cursor-progress-hover'
                            src={tempUrl}
                            alt={`dropzone-${id}`}
                            style={{
                                opacity: uploadState.state === UploadStates.done ? '1' : '0.5'
                            }}
                        />
                        : <div id={id} className='background-grey-1 cursor-progress-hover rounded-xxl'
                               style={{
                                   width: '100px',
                                   height: '100px',
                                   opacity: uploadState.state === UploadStates.done ? '1' : '0.5'
                               }}/>
                }
            </div>
        );
    }


    const dropZone = (
        <DropZone
            disabled={disabled}
            identifier={'documentation'}
            accept={accept}
            id={id}
            error={hasError}
            onSuccess={onSuccess}
            multiple={multiple}
            setTempUrl={setTempUrl}
            loading={loading}
            uploading={uploading}
            setLoading={setLoading}
            openOnClick={!url?.length}
            className={`row no-gutters align-items-center justify-content-center `}
            noParse>
            {(openFileDialog, {notAccepted}) =>
                uploading
                    ? renderUploadProgress()
                    : url?.length > 0
                        ? renderFile()
                        : (
                            <Row
                                className={`w-100 no-gutters align-items-center justify-content-center ${uploading ? 'cursor-progress-hover' : ''} ${disabled || loading ? 'cursor-not-allowed-hover' : 'cursor-pointer-hover'}`}>
                                <Col xs={12} className={'d-flex flex-column justify-content-center align-items-center'}>
                                    <ImagePlaceholder className={'placeholder-image'}/>
                                    {
                                        !notAccepted &&
                                        <p className={'placeholder-text'}>
                                            Drag and Drop the file here
                                            <br/>
                                            File format: {accept.join(', ')}
                                        </p>
                                    }
                                    {
                                        notAccepted &&
                                        <p className={'placeholder-text not-accepted'}>
                                            That is not the correct format. the correct format are:
                                            <br/>
                                            File format: {accept.join(', ')}
                                        </p>
                                    }
                                </Col>
                                <Button
                                    className='py-2 px-4'
                                    color={'primary'}
                                    variant={'text'}
                                    onClick={openFileDialog}
                                >
                                    {hasError ? "Retry" : "Choose File"}
                                </Button>
                            </Row>
                        )
            }
        </DropZone>
    )

    return (
        <>
            {
                url?.length > 0
                    ? <Removable onRemove={remove}>
                        {dropZone}
                    </Removable>
                    : dropZone
            }
        </>


    );
}

export default AddWarrantyAndReturnDocumentationDropZone;
